import React, { useEffect, useState } from "react";
import { api, fileDownloadUrl, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileAudio, Image as ImageIcon, Paperclip, Archive } from "lucide-react";

export default function Files() {
  const { user } = useAuth();
  const canExport = ["super_admin", "operations"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ from: "", to: "" });
  const [ftype, setFtype] = useState("ALL");
  useEffect(() => { api.get("/files").then((r) => setRows(r.data)); }, []);
  const filtered = ftype === "ALL" ? rows : rows.filter((f) => f.file_type === ftype);

  const icon = (t) => (t === "audio" ? <FileAudio className="h-4 w-4 text-primary" /> : t === "artwork" ? <ImageIcon className="h-4 w-4 text-emerald-400" /> : <Paperclip className="h-4 w-4 text-muted-foreground" />);

  const exportUrl = () => {
    const params = new URLSearchParams();
    if (range.from) params.append("from_date", range.from);
    if (range.to) params.append("to_date", range.to);
    params.append("auth", getToken());
    return `${API}/exports/approved-assets?${params.toString()}`;
  };

  return (
    <div className="space-y-6" data-testid="files-page">
      <div><h1 className="font-heading text-3xl font-bold tracking-tight">Files</h1><p className="mt-1 text-muted-foreground">Secure private files. Access restricted to authorised users.</p></div>
      {canExport && (
        <Card className="p-5" data-testid="bulk-export-card">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold"><Archive className="h-5 w-5 text-primary" />Bulk Export — Approved Assets</h3>
          <p className="mb-3 text-sm text-muted-foreground">Download all approved/live release assets (audio + artwork) in one ZIP. Leave dates empty to export everything approved.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">From (release date)</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="export-from" className="mt-1 w-44" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="export-to" className="mt-1 w-44" /></div>
            <a href={exportUrl()} download><Button data-testid="bulk-export-btn"><Download className="mr-2 h-4 w-4" />Download ZIP</Button></a>
          </div>
        </Card>
      )}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Filter by type:</span>
        <Select value={ftype} onValueChange={setFtype}>
          <SelectTrigger className="w-52" data-testid="file-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["ALL", "audio", "artwork", "kyc", "contract", "ticket_attachment"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Filename</TableHead><TableHead>Release</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No files.</TableCell></TableRow>}
            {filtered.map((f) => (
              <TableRow key={f.id} data-testid={`file-row-${f.id}`}>
                <TableCell><Badge variant="outline" className="gap-1">{icon(f.file_type)}{f.file_type}</Badge></TableCell>
                <TableCell className="font-medium">{f.display_name || f.original_filename}</TableCell>
                <TableCell>{f.release_title || "—"}</TableCell>
                <TableCell>{((f.size || 0) / 1024 / 1024).toFixed(2)} MB</TableCell>
                <TableCell className="text-right">
                  <a href={fileDownloadUrl(f.id)} download={f.display_name || f.original_filename}>
                    <Button size="sm" variant="secondary" data-testid={`download-file-${f.id}`}><Download className="mr-2 h-4 w-4" />Download</Button>
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
